const PictViewClass = require('pict-view');
const DefaultConfiguration = require('../configuration/HeadlightPanels-Configuration-Default.json');

const libHeadlightPaneValidations = require('./HeadlightPanels-Pane-Validations.js');
const libDocumentStateDisplay = require('../behaviors/DocumentStateDisplay.js');
const libHeadlightPaneNavigation = require('./HeadlightPanels-Pane-Navigation.js');
const libHeadlightPaneObservations = require('./HeadlightPanels-Pane-Observations.js');
const libHeadlightPaneSecret = require('./HeadlightPanels-Pane-Secret.js');
const libHeadlightPaneProfile = require('./HeadlightPanels-Pane-Profile.js');
const libHeadlightPaneSettings = require('./HeadlightPanels-Pane-Settings.js');
const libHeadlightPaneSolver = require('./HeadlightPanels-Pane-Solver.js');
const libPictFormSolver = require('./Pict-Form-Solver.js');
const libHeadlightPaneStatus = require('./HeadlightPanels-Pane-Status.js');
const libHeadlightPaneBridges = require('./HeadlightPanels-Pane-Bridges.js');
const libHeadlightPaneDocuments = require('./HeadlightPanels-Pane-Documents.js');
const libHeadlightPaneSamples = require('./HeadlightPanels-Pane-Samples.js');
const libHeadlightPaneCustom = require('./HeadlightPanels-Pane-Custom.js');
const libHeadlightPanelsFableService = require('../services/HeadlightPanels-Fable-Service.js');
const libHeadlightModalService = require('../../../../lib/web/headlight-modal/Headlight-Modal-Service.js');
const libMaterialsFormManagerService = require('../../../../lib/service/materials-form-manager/Materials-Form-Manager.js');
const libHeadlightPopoverService = require('../../../../lib/web/headlight-popover/Headlight-Popover-Service.js');
const libNativeBridgeService = require('../../../../lib/web/native-bridge/index.js');
const libEntityProvider = require('../../../../web/providers/Entity-Provider.js');
const libQRPanel = require('./QRPanel.js');
const libHeadlightToastAdapter = require('../behaviors/HeadlightToastAdapter.js');

// Where remembered pane widths live between sessions. They are a cosmetic hint for the loading
// skeleton only -- nothing behaves differently when they are missing, stale or unreadable.
const _PaneWidthStorageKey = 'HeadlightPanels.PaneWidths';

/** @typedef {import('pict') & import('fable') &
 *   {
 *     log: any,
 *     instantiateServiceProviderWithoutRegistration: (hash: String) => any,
 *     instantiateServiceProviderIfNotExists: (hash: string) => any,
 *     TransactionTracking: import('pict/types/source/services/Fable-Service-TransactionTracking'),
 *     MetaTemplate: any,
 *     fable: import('fable'),
 *     HeadlightPanels: import('../services/HeadlightPanels-Fable-Service.js'),
 *     HeadlightRestClient: import('headlight-rest-client'),
 *     BridgeDocumentManager: import('../../../../lib/service/headlight-bridge-document-manager'),
 *     PictApplication: InstanceType<import('headlight-document-application')>,
 *     HeadlightModal: libHeadlightModalService,
 *	   HeadlightPopover: libHeadlightPopoverService,
 *     views: {
 *       HeadlightPanels: HeadlightPanels,
 *     },
 *   }
 * } Pict */
class HeadlightPanels extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {Pict} */
		this.pict;
		// set the default configuration for the view
		this.setDefaultPanelConfiguration();
		// panel states
		this.LeftPanelActive = false;
		this.LeftPanelPinned = false;
		this.LeftPanelMode = '';
		this.LeftPanelView = '';
		this.RightPanelActive = false;
		this.RightPanelPinned = false;
		this.RightPanelMode = '';
		this.RightPanelView = '';
		this.MinimumInputLengthForSearch = 0;
		// Width of each pane the last time it rendered, so the loading skeleton can hold the panel at the
		// width the content is about to need. The wrapper shrinks to fit, and panes vary widely (a
		// Navigation pane is ~230px where Observations hits the 50vw cap), so there is no useful default.
		this.PaneWidths = this.loadPaneWidths();

		// Adapter that translates legacy object-first toast options into Pict-Section-Modal
		// toast calls (type mapping, single-toast, duplicate throttle, HTML handling).
		this._ToastAdapter = new libHeadlightToastAdapter({ ThrottleWindowMS: 1000 });

		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
		this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });

		this.pict.MetaTemplate.addPattern('{~CacheBustPostfix:', '~}', (pHash, pData, fCallback) =>
		{
			return `?t=${ new Date().getTime() }`;
		});

		// add the ".is-hidden" class to the element if the browser is iOS and the passed value is true
		this.pict.MetaTemplate.addPattern('{~HideOniOS:', '~}', (pHash, pData, fCallback) =>
		{
			// do we want to hide the element T/F value
			const tmpHideValue = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			// check if the URL param flag is set to hide the download button by parent apps (SmartForms, etc.)
			const tmpURLParamFlag = new URLSearchParams(window.location.search).get('hideDownloadButton');
			// check if the user agent is iOS (works for browsers, not webview apps)
			const tmpIsIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
			// if the hide value is true and the URL param flag is set or the user agent is iOS, return the hidden
			return tmpHideValue && (tmpURLParamFlag || tmpIsIOS) ? 'is-hidden' : '';
		});

		// add the ".is-hidden" class to the element if this page cannot create a new document (Clone, Duplicate)
		this.pict.MetaTemplate.addPattern('{~HideIfCannotCreateDocument', '~}', (pHash, pData, fCallback) =>
		{
			const tmpDocumentProvider = this.pict.providers.DocumentProvider;
			return tmpDocumentProvider && !tmpDocumentProvider.canCreateDocument() ? 'is-hidden' : '';
		});

		// add the ".is-hidden" class to the element if the passed value is true
		this.pict.MetaTemplate.addPattern('{~Hide:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpValue = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return tmpValue ? 'is-hidden' : '';
		});

		// add the ".is-hidden" class to the element if the passed value is false
		this.pict.MetaTemplate.addPattern('{~Show:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpValue = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return tmpValue ? '' : 'is-hidden';
		});

		// do return the value in span tag if the passed value is true and the value is not empty
		this.pict.MetaTemplate.addPattern('{~If:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpValue = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			const tmpDataId = pData && pData.id ? `id="${pData.id}-Text"` : '';
			return tmpValue && tmpValue.length ? `<span ${tmpDataId}>` + tmpValue + '</span>' : '';
		});

		// A document's workflow state as a person should read it. The approval workflow authors a
		// display name per state (`StateName` - "In Test", "Waiting for District Lab Engineer
		// Approval"); `Status` is the machine hash the workflow keys off and was never meant for
		// the screen. Drafts are created by the API without consulting the workflow, so they carry
		// a Status and no StateName - the fallback is what keeps those from rendering blank.
		//
		// Takes the address of the RECORD, not of a field: `{~DocumentState:Record~}`.
		this.pict.MetaTemplate.addPattern('{~DocumentState:', '~}', (pHash, pData, fCallback) =>
		{
			return libDocumentStateDisplay.resolveDocumentStateDisplay(this.pict.resolveStateFromAddress(pHash, pData, fCallback));
		});

		// replace the value with a mapped css class from the Observation Provider
		this.pict.MetaTemplate.addPattern('{~LabelClass:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return this.pict.providers.ObservationProvider.getLabelClassFromObservationTagObject(tmpRecord);
		});

		// replace the observation type with a mapped display name from the Observation Provider
		this.pict.MetaTemplate.addPattern('{~ObsTypeDisplay:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return this.pict.providers.ObservationProvider.getObservationMappedDetails(tmpRecord)?.Type || pData;
		});

		// take a code string and return a canvas QR code image
		this.pict.MetaTemplate.addPattern('{~QRCodeImage:', '~}', (pHash, pData, fCallback) =>
		{
			// if the record has a size option from the template string, use that build the QR code image
			// EX: "{~QRCodeImage:Record^128x128^~}" will return a 128x128 QR code image
			const tmpType = pHash.includes('^') && pHash.split('^')?.length > 2 ? pHash.split('^')[2] : null;
			const tmpSize = pHash.includes('^') ? pHash.split('^')[1] : null;
			const tmpHash = pHash.includes('^') ? pHash.split('^')[0] : pHash;
			const tmpRecord = this.pict.resolveStateFromAddress(tmpHash, pData, fCallback);
			if (!tmpRecord)
			{
				return '';
			}
			let tmpHeight;
			let tmpWidth;
			// if we have a size option, make sure it's in the correct format
			if (tmpSize && !tmpSize.includes('x'))
			{
				this.pict.fable.log.error('Invalid QR code size. Please use the format "128x128"');
				return '';
			}
			else
			{
				tmpHeight = tmpSize ? tmpSize.split('x')[1] : '128';
				tmpWidth = tmpSize ? tmpSize.split('x')[0] : '128';
			}
			// create the QR code canvas and return the image tag, adding a white border around the image
			const tmpCanvas = this.pict.providers.QRCodeProvider.create(tmpRecord, { width: tmpWidth, height: tmpHeight, type: tmpType });
			return `<img style="outline: 1px solid white" src="${tmpCanvas}" alt="QR Code" />`;
		});

		this.pict.MetaTemplate.addPattern('{~GoogleMapURL:', '~}', (pHash, pData, fCallback) =>
		{
			// if the record has a size option from the template string, use that to get the map URL
			// EX: "{~GoogleMapURL:Record^128x128^~}" will return the URL for a 128x128 map
			const tmpSize = pHash.includes('^') ? pHash.split('^')[1] : null;
			const tmpHash = pHash.includes('^') ? pHash.split('^')[0] : pHash;
			const tmpRecord = this.pict.resolveStateFromAddress(tmpHash, pData, fCallback);
			// pass the size option to the provider if it exists, it will handle the fallback if not
			return this.pict.providers.UtilityProvider.getGoogleMapURLFromEntityRecord(tmpRecord, { size: tmpSize });
		});

		// watch for the escape key to close the active panel(s)
		document.body.addEventListener('keydown', (event) =>
		{
			// close the modal or the panels on escape key press
			if (event.key === 'Escape')
			{
				if (this.AppData.FullScreenModalActive)
				{
					this.togglePanelFullScreenModal('close');
				}
				else
				{
					this.closeAllUnpinnedPanels();
				}
			}
		});
		// shared providers for core entities
		this.pict.addProvider('EntityDocumentProvider', { Entity: 'Document' }, libEntityProvider);
		this.pict.addProvider('EntityInspectionDocumentProvider', { Entity: 'InspectionDocumentJoin' }, libEntityProvider);
		this.pict.addProvider('EntityInspectionTypeProvider', { Entity: 'InspectionType' }, libEntityProvider);
		this.pict.addProvider('ConfigurationProvider', {}, require('../../../../web/providers/ConfigurationProvider.js'));
		this.pict.addProvider('UploadTracking', {}, require('../../../../web/providers/UploadTrackingProvider.js'));
		this.pict.addProvider('UtilityProvider', {}, require('../../../../web/providers/UtilityProvider.js'));
		this.pict.addProvider('DocumentProvider', {}, require('../../../../web/providers/DocumentProvider.js'));
		this.pict.addProvider('TestInstanceProvider', {}, require('../../../../web/providers/TestInstanceProvider.js'));
		this.pict.addProvider('ProjectProvider', {}, require('../../../../web/providers/ProjectProvider.js'));
		this.pict.addProvider('ObservationProvider', {}, require('../../../../web/providers/ObservationProvider.js'));
		this.pict.addProvider('LocationProvider', {}, require('../../../../web/providers/LocationProvider.js'));
		this.pict.addProvider('SampleProvider', {}, require('../../../../web/providers/SampleProvider.js'));
		this.pict.addProvider('SampleLogProvider', {}, require('../../../../web/providers/SampleLogProvider.js'));
		this.pict.addProvider('GeoProvider', {}, require('../../../../web/providers/GeoProvider.js'));
		this.pict.addProvider('FieldSettingsProvider', {}, require('../../../../web/providers/FieldSettingsProvider.js'));
		this.pict.addProvider('MaterialProvider', {}, require('../../../../web/providers/MaterialProvider.js'));
		this.pict.addProvider('MixDesignProvider', {}, require('../../../../web/providers/MixDesignProvider.js'));
		this.pict.addProvider('UserProvider', {}, require('../../../../web/providers/UserProvider.js'));
		this.pict.addProvider('MarkdownProvider', {}, require('../../../../web/providers/MarkdownProvider.js'));
		this.pict.addProvider('QRCodeProvider', {}, require('../../../../web/providers/QRCodeProvider.js'));
		this.registerScanHandling();
		this.pict.addProvider('ObservationSetProvider', {}, require('../../../../web/providers/ObservationSetProvider.js'));
		this.pict.addProvider('ImportantFilesProvider', {}, require('../../../../web/providers/ImportantFilesProvider.js'));
		this.pict.addProvider('FormConversionProvider', {}, require('../../../../web/providers/FormConversionProvider.js'));
		this.pict.addProvider('FileNavigatorProvider', {}, require('../../../../web/providers/FileNavigatorProvider.js'));
		// TODO: how do we determine if need these included here vs just in dynamic forms?
		this.pict.addProvider('MixSpecificationProvider', {}, require('../../../../web/providers/MixSpecificationProvider.js'));
		this.pict.addProvider('ProductProvider', {}, require('../../../../web/providers/ProductProvider.js'));
		this.pict.addProvider('ProducerProvider', {}, require('../../../../web/providers/ProducerProvider.js'));
		this.pict.addProvider('PlantProvider', {}, require('../../../../web/providers/PlantProvider.js'));
		this.pict.addProvider('LabProvider', {}, require('../../../../web/providers/LabProvider.js'));
		this.pict.addProvider('LineItemProvider', {}, require('../../../../web/providers/LineItemProvider.js'));
		this.pict.addProvider('ProjectFileProvider', {}, require('../../../../web/providers/ProjectFileProvider.js'));
		// TODO: when CommentEntityJoin is complete - I think this needs to move to CORE
		this.pict.addProvider('CommentProvider', {}, require('../../../../web/providers/CommentProvider.js'));
		this.pict.addProvider('InspectionProvider', {}, require('../../../../web/providers/InspectionProvider.js'));

		// Isolated Views (these could be moved to the new location - are not exclusive to the panels)
		this.pict.addView('ObservationIncluded', {}, require('../views/Observation-View-Included.js'));
		this.pict.addView('ObservationCreateMedia', {}, require('../views/Observation-View-Create-Media.js'));
		this.pict.addView('ObservationSearch', {}, require('../views/Observation-View-Search.js'));
		this.pict.addView('ObservationDetails', {}, require('../views/Observation-View-Details.js'));
		this.pict.addView('ObservationAnnotation', {}, require('../views/Observation-Edit-Annotation.js'));
		this.pict.addView('AcademyView', {}, require('../views/Academy-View.js'));
		this.pict.addView('Inspection', {}, require('../views/Inspection-View.js'));
		this.pict.addView('ObservationSubImageSelector', {}, require('../views/Observation-Edit-SubImage.js'));
		this.pict.addView('TestInstanceWizard', {}, require('../views/TestInstance-Wizard.js'));
		this.pict.addView('ObservationSet', {}, require('./Observation-View-Sets.js'));
		this.pict.addView('SampleSearch', {}, require('../views/Sample-View-Search.js'));
		this.pict.addView('SampleDetails', {}, require('../views/Sample-View-Details.js'));
		this.pict.addView('SampleCreate', {}, require('../views/Sample-View-Create.js'));
		this.pict.addView('SampleScan', {}, require('../views/Sample-View-Scan.js'));
		this.pict.addView('SampleCurrent', {}, require('../views/Sample-View-Current.js'));
		this.pict.addView('DocumentSearch', {}, require('../views/Document-View-Search.js'));
		this.pict.addView('DocumentCreate', {}, require('../views/Document-View-Create.js'));
		this.pict.addView('DocumentCurrent', {}, require('../views/Document-View-Current.js'));
		this.pict.addView('DocumentDetails', {}, require('../views/Document-View-Details.js'));
		this.pict.addView('ProjectFileSearch', {}, require('../views/ProjectFile-View-Search.js'));
		this.pict.addView('CommentCurrent', {}, require('../views/Comment-View-Current.js'));
		this.pict.addView('PictFormsSolver', libPictFormSolver.default_configuration, libPictFormSolver);
		// Views
		this.pict.addView('HeadlightPaneValidations', libHeadlightPaneValidations.default_configuration, libHeadlightPaneValidations);
		this.pict.views.HeadlightPaneValidations.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneNavigation', libHeadlightPaneNavigation.default_configuration, libHeadlightPaneNavigation);
		this.pict.views.HeadlightPaneNavigation.options.DefaultDestinationAddress = '#HeadlightPanels-RightPanel';
		this.pict.addView('HeadlightPaneObservations', libHeadlightPaneObservations.default_configuration, libHeadlightPaneObservations);
		this.pict.views.HeadlightPaneObservations.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneSecret', libHeadlightPaneSecret.default_configuration, libHeadlightPaneSecret);
		this.pict.views.HeadlightPaneSecret.options.DefaultDestinationAddress = '#HeadlightPanels-RightPanel';
		this.pict.addView('HeadlightPaneProfile', libHeadlightPaneProfile.default_configuration, libHeadlightPaneProfile);
		this.pict.views.HeadlightPaneProfile.options.DefaultDestinationAddress = '#HeadlightPanels-RightPanel';
		this.pict.addView('HeadlightPaneSettings', libHeadlightPaneSettings.default_configuration, libHeadlightPaneSettings);
		this.pict.views.HeadlightPaneSettings.options.DefaultDestinationAddress = '#HeadlightPanels-RightPanel';
		this.pict.addView('HeadlightPaneSolver', libHeadlightPaneSolver.default_configuration, libHeadlightPaneSolver);
		this.pict.views.HeadlightPaneSolver.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneStatus', libHeadlightPaneStatus.default_configuration, libHeadlightPaneStatus);
		this.pict.views.HeadlightPaneStatus.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneBridges', libHeadlightPaneBridges.default_configuration, libHeadlightPaneBridges);
		this.pict.views.HeadlightPaneBridges.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneDocuments', libHeadlightPaneDocuments.default_configuration, libHeadlightPaneDocuments);
		this.pict.views.HeadlightPaneDocuments.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneSamples', libHeadlightPaneSamples.default_configuration, libHeadlightPaneSamples);
		this.pict.views.HeadlightPaneSamples.options.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('QRPanel', libQRPanel.default_configuration, libQRPanel);
		// Custom Panels
		const tmpCustomLeftPanelConfiguration = JSON.parse(JSON.stringify(libHeadlightPaneCustom.default_configuration));
		tmpCustomLeftPanelConfiguration.PanelName = 'Left';
		tmpCustomLeftPanelConfiguration.PanelTitle = 'Left Panel';
		tmpCustomLeftPanelConfiguration.PanelIcon = 'fa-circle-notch';
		tmpCustomLeftPanelConfiguration.PanelMode = '';
		tmpCustomLeftPanelConfiguration.PanelView = '';
		tmpCustomLeftPanelConfiguration.DefaultDestinationAddress = '#HeadlightPanels-LeftPanel';
		this.pict.addView('HeadlightPaneCustomLeft', tmpCustomLeftPanelConfiguration, libHeadlightPaneCustom);

		const tmpCustomRightPanelConfiguration = JSON.parse(JSON.stringify(libHeadlightPaneCustom.default_configuration));
		tmpCustomRightPanelConfiguration.PanelName = 'Right';
		tmpCustomRightPanelConfiguration.PanelTitle = 'Right Panel';
		tmpCustomRightPanelConfiguration.PanelIcon = 'fa-circle-notch';
		tmpCustomRightPanelConfiguration.PanelMode = '';
		tmpCustomRightPanelConfiguration.PanelView = '';
		tmpCustomRightPanelConfiguration.DefaultDestinationAddress = '#HeadlightPanels-RightPanel';
		this.pict.addView('HeadlightPaneCustomRight', tmpCustomRightPanelConfiguration, libHeadlightPaneCustom);

		// Fable Service
		this.fable.addAndInstantiateServiceTypeIfNotExists('HeadlightPanels', libHeadlightPanelsFableService);
		this.fable.addAndInstantiateServiceTypeIfNotExists('HeadlightModal', libHeadlightModalService);
		this.fable.addAndInstantiateServiceTypeIfNotExists('MaterialsFormManager', libMaterialsFormManagerService);
		this.fable.addAndInstantiateServiceTypeIfNotExists('NativeBridge', libNativeBridgeService);
		this.fable.addAndInstantiateServiceTypeIfNotExists('HeadlightPopover', libHeadlightPopoverService);
		this.pict.serviceManager.addAndInstantiateServiceType('BridgeDocumentManager', require('../../../../lib/service/headlight-bridge-document-manager'));
	}

	onAfterInitialize()
	{
		this.pict.CSSMap.injectCSS();

		return super.onAfterInitialize();
	}

	/**
	 * Lifecycle hook that triggers before the view is rendered.
	 *
	 * @param {import('pict-view').Renderable} pRenderable - The renderable that will be rendered.
	 */
	onBeforeRender(pRenderable)
	{
		this.pict.fable.log.info('Headlight Panels view onBeforeRender');

		this.pict.views.ObservationCreateMedia.instantiateConfiguration();

		this.pict.services.HeadlightPanels.checkIfPhoneDevice();

		// Apply the preferences that shape the first paint (theme, page width, navbar position) BEFORE
		// the template runs -- the navbar's position is read out of the configuration at render time, so
		// doing this afterwards makes the user watch it jump. The values are already in AppData, so this
		// costs nothing and waits on nothing.
		this.pict.views.HeadlightPaneSettings.applyPreRenderUserPreferences();

		return super.onBeforeRender(pRenderable);
	}

	/**
	 * Lifecycle hook that triggers after the view is rendered (async flow).
	 *
	 * @param {import('pict-view').ErrorCallback} fCallback - The callback to call when the async operation is complete.
	 * @param {import('pict-view').Renderable} pRenderable - The renderable that was rendered.
	 */
	onAfterRenderAsync(fCallback, pRenderable)
	{
		this.pict.fable.log.info('Headlight Panels view onAfterRenderAsync');

		// initialize context specific elements. Will show or hide based on the current context
		const isDocumentContext = this.pict.AppData?.DocumentData?.ReportData;
		isDocumentContext ? this.toggleContextSpecficElements('show') : this.toggleContextSpecficElements('hide');

		// set the body element offset for the fixed navbar
		this.setBodyElementOffsetForFixedNavbar();

		this.adjustBottomToolbarOptions();

		// The navbar renders the Current Samples button hidden by default. Re-apply the known state first
		// (synchronously, so a later render cannot clobber it), then refresh it from the server.
		this.applyCurrentSamplesButtonState();
		this.syncCurrentSamplesButtonState();
		// The navbar render recreates the Save button and the (empty) auto-save indicator container.
		this.syncSaveButtonState();
		this.syncAutoSaveStatus();

		// login expired, redirect to login page
		if (!this.AppData.CurrentUser)
		{
			this.pict.fable.log.warn('No user found. Unable to load user settings.');
		}
		else
		{
			// load the user settings for the panels
			this.pict.views.HeadlightPaneSettings.loadUserPreferences();
		}

		return super.onAfterRenderAsync(fCallback, pRenderable);
	}

	/**
	 * Toggle the class name based on the action provided
	 * @param {HTMLElement} pElement - The element to toggle the class on
	 * @param {string} pAction - The action to take on the element (show, hide, toggle)
	 * @return {void}
	 */
	_toggleElementClass(pElement, pAction)
	{
		if (pAction === 'show')
		{
			pElement.classList.remove('is-hidden');
		}
		else if (pAction === 'hide')
		{
			pElement.classList.add('is-hidden');
		}
		else
		{
			pElement.classList.toggle('is-hidden');
		}
	}

	/**
	 * Some elements are hidden or shown based on the context of the content. This method toggles the visibility of these elements.
	 * @param {string} pAction - The action to take on the context specific elements (show, hide, toggle)
	 * @return {void}
	 */
	toggleContextSpecficElements(pAction)
	{
		const tmpNavBar = document.getElementById('HeadlightPanels-Navbar-Brand-Container');
		const tmpSaveButton = document.getElementById('HeadlightPanels-NavBar-Save-Button')?.parentElement;
		const tmpSolverButton = document.getElementById('HeadlightPanels-NavBar-Solver-Button')?.parentElement;
		const tmpFullScreenButton = document.getElementById('HeadlightPanels-Navbar-FullScreen-Button')?.parentElement;
		if (tmpNavBar)
		{
			this._toggleElementClass(tmpNavBar, pAction);
		}
		if (tmpSaveButton)
		{
			this._toggleElementClass(tmpSaveButton, pAction);
		}
		if (tmpSolverButton)
		{
			this._toggleElementClass(tmpSolverButton, pAction);
		}
		if (tmpFullScreenButton)
		{
			this._toggleElementClass(tmpFullScreenButton, pAction);
		}
	}

	/**
	 * Update the navbar once a document has been selected (or removed)
	 * @return {void}
	 */
	updatePanelsBasedOnDocumentContext()
	{
		// if this is a document/form context, show the approval actions branding and document save button
		const isDocumentContext = this.pict.AppData?.DocumentData?.ReportData;
		// if the status panel is enabled, update the approvals style state
		if (isDocumentContext && this.pict.settings.HeadlightPanels.Configuration.PanelList.find((panel) => panel.name === 'status'))
		{
			this.pict.views.HeadlightPaneStatus.updateApprovalsStyleState();
		}
		// re-render the buttons and the branding now that we have the document context
		this.pict.views.HeadlightPanels.renderAsync('HeadlightPanels-NavBar-Brand', null, null, null, () =>
		{
			this.pict.fable.log.info('Re-rendered the navbar branding');

			this.pict.views.HeadlightPanels.renderAsync('HeadlightPanels-NavBar-Button-Wrapper', null, null, null, () =>
			{
				this.pict.fable.log.info('Re-rendered the navbar buttons');
				// adjust the bottom toolbar options based on the context
				if (isDocumentContext)
				{
					this.toggleContextSpecficElements('show');
				}
				else
				{
					this.toggleContextSpecficElements('hide');
				}
				// the re-render above replaces the button element, so re-apply the locked/saving state
				this.syncSaveButtonState();
				// ...and empties the auto-save indicator's container along with it.
				this.syncAutoSaveStatus();
				this.applyCurrentSamplesButtonState();
				this.syncCurrentSamplesButtonState();
			});
		});
	}

	/**
	 * Reflect the current save/approval/locked state on the navbar Save button.
	 *
	 * Called both on every save-latch transition and from the navbar render path, since re-rendering the
	 * button wrapper replaces the element and drops any attribute set on the previous one. Derives entirely
	 * from state, so it is safe to call before the button exists — the next render picks it up.
	 * @return {void}
	 */
	syncSaveButtonState()
	{
		const tmpSaveButton = document.getElementById('HeadlightPanels-NavBar-Save-Button');
		if (!tmpSaveButton)
		{
			return;
		}
		const tmpDocumentProvider = this.pict.providers?.DocumentProvider;
		const tmpIsSaving = Boolean(tmpDocumentProvider?.IsSaving);
		const tmpIsTakingAction = Boolean(tmpDocumentProvider?.isTakingAction);
		const tmpIsLocked = Boolean(this.pict.AppData?.DocumentData?.ReportData?.Locked);
		// is-loading stays keyed to the save alone: a spinner on Save while the user is watching an
		// *approval* land reads as "it is saving", which is not what is happening.
		tmpSaveButton.classList.toggle('is-loading', tmpIsSaving);
		tmpSaveButton.setAttribute('aria-busy', (tmpIsSaving || tmpIsTakingAction) ? 'true' : 'false');
		if (tmpIsSaving || tmpIsTakingAction || tmpIsLocked)
		{
			tmpSaveButton.setAttribute('disabled', 'disabled');
			// The document is not locked yet during an approval action, so that reason outranks the others.
			tmpSaveButton.setAttribute('title', tmpIsTakingAction ? 'An approval action is in progress.' : (tmpIsLocked ? 'Document is locked and can not be saved.' : 'Saving...'));
		}
		else
		{
			tmpSaveButton.removeAttribute('disabled');
			tmpSaveButton.removeAttribute('title');
		}
	}

	/**
	 * Repaint the auto-save indicator that sits beside the navbar Save button.
	 *
	 * The AutoSave provider owns the state and pushes it into `AppData.AutoSave`; this only asks the
	 * template to redraw. Called on every auto-save state transition and from the navbar render path,
	 * because re-rendering the button wrapper replaces the container element. A no-op when the
	 * container is absent (a host application whose navbar has no Save button).
	 * @return {void}
	 */
	syncAutoSaveStatus()
	{
		if (!document.getElementById('HeadlightPanels-Navbar-AutoSaveStatus-Container'))
		{
			return;
		}
		// A one-shot parse rather than render('...'): this fires on every auto-save transition, and the
		// view's own render lifecycle re-instantiates the media configuration and re-probes the device
		// on the way through. None of that belongs on a 90-second timer.
		const tmpStatusHTML = this.pict.parseTemplateByHash('HeadlightPanels-NavBar-AutoSaveStatus', {});
		this.pict.ContentAssignment.assignContent('#HeadlightPanels-Navbar-AutoSaveStatus-Container', tmpStatusHTML);
	}

	/**
	 * Paint the navbar Current Samples button from the cached lookup result.
	 *
	 * Deliberately synchronous, and separate from the lookup: the navbar wrapper re-renders from
	 * configuration (which defaults the button to is-hidden) and replaces the element, so the state has to
	 * be re-applied on every render. An async apply loses that race whenever a render lands between the
	 * lookup starting and finishing -- which is exactly how this button ended up intermittently hidden.
	 * @return {void}
	 */
	applyCurrentSamplesButtonState()
	{
		const tmpWrapper = document.getElementById('HeadlightPanels-NavBar-CurrentSamples-Button')?.parentElement;
		if (!tmpWrapper)
		{
			return;
		}
		tmpWrapper.classList.toggle('is-hidden', !this.HasJoinedSamples && !this.hasFormHeldSamples());
	}

	/**
	 * Whether the open form holds a Sample with a QR code, saved or not. A form that mints its Sample on load
	 * has one before the first save writes the join, and the Current tab lists and prints it from the form.
	 * @return {boolean}
	 */
	hasFormHeldSamples()
	{
		const tmpSampleProvider = this.pict.providers.SampleProvider;
		if (!tmpSampleProvider || typeof tmpSampleProvider.getFormHeldSamples !== 'function')
		{
			return false;
		}
		try
		{
			return tmpSampleProvider.getFormHeldSamples().length > 0;
		}
		catch (pError)
		{
			this.log.warn(`Could not read the Samples this form holds for the Current Samples button: ${pError?.message || pError}`);
			return false;
		}
	}

	/**
	 * Refresh whether this document has Samples joined to it, then paint the button.
	 *
	 * The DocumentPolyJoin rows are written server-side, so a Sample created on this form only shows up
	 * after a save round-trip -- hence this runs from the save-drain completion as well as the render path.
	 * Until then a Sample the form holds keeps the button visible (see hasFormHeldSamples). A failed lookup
	 * falls back to that alone rather than surfacing an error: it is an optional shortcut.
	 * @return {Promise<void>}
	 * @async
	 */
	async syncCurrentSamplesButtonState()
	{
		if (this._CurrentSamplesLookupInFlight)
		{
			return;
		}
		const tmpIDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
		let tmpHasSamples = false;
		if (tmpIDDocument)
		{
			this._CurrentSamplesLookupInFlight = true;
			try
			{
				const tmpJoins = await this.pict.providers.SampleProvider.getDocumentSampleJoins(tmpIDDocument);
				tmpHasSamples = Array.isArray(tmpJoins) && tmpJoins.length > 0;
			}
			catch (pError)
			{
				this.log.warn(`Could not resolve joined Samples for the Current Samples button: ${pError?.message || pError}`);
			}
			finally
			{
				this._CurrentSamplesLookupInFlight = false;
			}
		}
		this.HasJoinedSamples = tmpHasSamples;
		this.applyCurrentSamplesButtonState();
	}

	/**
	 * This method adds loading skeleton rows to the specified container.
	 * @param {string} pContainerID - The ID of the container to add the skeleton rows to.
	 * @param {number} [pRowCount] - The number of skeleton rows to add.
	 * @param {number} [pMinHeight] - The minimum height of each skeleton row.
	 * @return {void}
	 */
	addLoadingSkeletonRows(pContainerID, pRowCount, pMinHeight)
	{
		const tmpRowCount = pRowCount || 3;
		const tmpMinHeight = pMinHeight || '4.5rem'; // Bulma default height
		const tmpContainer = document.getElementById(pContainerID);
		if (!tmpContainer)
		{
			this.log.error('Container not found. Please check the ID you provided for skeleton loading UI.');
			return;
		}
		// get the inner width of the container to set the gradient overlay width to reduce the horizontal flicker
		const currentElementInnerWidth = tmpContainer.clientWidth;
		// clear the container before adding the skeleton rows
		tmpContainer.innerHTML = '';
		// add a gradient overlay to the skeleton rows to make it look like an inifinite scroll
		let tmpContent = `<div class="results-loading-gradient-overlay"></div>`;
		for (let i = 0; i < tmpRowCount; i++)
		{
			tmpContent += `<div class="panel-block skeleton-block has-background-text-40 mx-3 mb-1" style="min-height: ${tmpMinHeight}"></div>`;
		}
		// add a div to the end of the skeleton rows with the previous width to prevent the horizontal flicker
		tmpContent += `<div style="width: ${currentElementInnerWidth}px;"></div>`;
		// add the skeleton rows to the container
		tmpContainer.insertAdjacentHTML('beforeend', tmpContent);
	}

	/**
	 * This function is used to adjust the bottom toolbar options based on the context of the content. If the
	 * content is a document, the bottom toolbar is hidden.
	 * @param {boolean} [pIsDocumentContext=false] - The context of the content
	 * @return {void}
	 */
	adjustBottomToolbarOptions(pIsDocumentContext = false)
	{
		const tmpBottomToolbar = document.getElementById('HeadlightPanels-NavBar');
		if (!tmpBottomToolbar)
		{
			this.log.error('Bottom toolbar not found. Please check the ID you provided.');
			return;
		}
		if (pIsDocumentContext)
		{
			tmpBottomToolbar.classList.add('is-hidden');
		}
		else
		{
			tmpBottomToolbar.classList.remove('is-hidden');
		}
	}

	/**
	 * This function is used to close the panel via the x icon for itself.
	 * @param {Event} event - The event object
	 * @param {string} pPanelName - The name of the panel to close
	 * @return {void}
	 */
	closeThisPanel(event, pPanelName)
	{
		// if this panel is pinned, do nothing
		let tmpEventTargetName;
		if (event)
		{
			// if the target is a child of a pinned panel, don't close the panel
			const parentPanel = window.jQuery(event.target).parents('.hl-panel-wrapper')?.[0];
			if (parentPanel?.classList.contains('pinned-open'))
			{
				this.log.info('Panel is pinned open. Ignoring close request.');
				return;
			}
			tmpEventTargetName = event ? window.jQuery(event.target).parents('article[id]')?.[0]?.id : null;
			// strip the panel name from the ID if it's a panel (yuck)
			if (tmpEventTargetName)
			{
				tmpEventTargetName = tmpEventTargetName.replace('HeadlightPanels-', '').replace('Panel', '').toLowerCase();
			}
		}
		const tmpPanelName = pPanelName || tmpEventTargetName;
		if (!tmpPanelName)
		{
			this.log.error('Panel not found. Please check the ID you provided.');
			return;
		}
		this.handlePanelToggle(tmpPanelName, 'close');
		this.fable.HeadlightPanels.clearUIHighlights();
	}

	/**
	 * This function is used to close all unpinned panels at once for background click and escape key hits.
	 * @return {void}
	 */
	closeAllUnpinnedPanels()
	{
		this.log.info('Closing all unpinned panels');
		// if the left panel has any pinned-open panels, don't close the wrapper
		const leftPanel = document.getElementById('HeadlightPanels-LeftPanel');
		if (leftPanel?.classList.contains('is-active') && !leftPanel?.classList.contains('pinned-open'))
		{
			this.togglePanel('left', 'close');
			this.LeftPanelActive = null;
		}
		// if the right panel has any pinned-open panels, don't close the wrapper
		const rightPanel = document.getElementById('HeadlightPanels-RightPanel');
		if (rightPanel?.classList.contains('is-active') && !rightPanel?.classList.contains('pinned-open'))
		{
			this.togglePanel('right', 'close');
			this.RightPanelActive = null;
		}
		// we assume that if the user is pressing escape/closing, they want to close any active media
		this.pict.providers.UtilityProvider.pauseAnyActiveMedia();
		this.fable.HeadlightPanels.clearUIHighlights();
	}

	/**
	 * This function is used to close all panels at once and discards all pinned states.
	 * @return {void}
	 */
	closeAllPanels()
	{
		this.log.info('Closing all panels, regardless of pinned state');
		// if the left panel has any pinned-open panels, don't close the wrapper
		const leftPanel = document.getElementById('HeadlightPanels-LeftPanel');
		if (leftPanel?.classList.contains('is-active'))
		{
			this.togglePanel('left', 'close');
			this.LeftPanelActive = null;
			this.LeftPanelPinned = false;
			document.body.style['marginLeft'] = '0';
		}
		// if the right panel has any pinned-open panels, don't close the wrapper
		const rightPanel = document.getElementById('HeadlightPanels-RightPanel');
		if (rightPanel?.classList.contains('is-active'))
		{
			this.togglePanel('right', 'close');
			this.RightPanelActive = null;
			this.RightPanelPinned = false;
			document.body.style['marginRight'] = '0';
		}
		// we assume that if the user is pressing escape/closing, they want to close any active media
		this.pict.providers.UtilityProvider.pauseAnyActiveMedia();
		this.fable.HeadlightPanels.clearUIHighlights();
	}

	/**
	 * This function is used to close all panels without prompting to save changes
	 * @return {void}
	 */
	closeAllPanelsWithoutPrompt()
	{
		this.log.info('Closing all panels, regardless of pinned state');
		// if the left panel has any pinned-open panels, don't close the wrapper
		const leftPanel = document.getElementById('HeadlightPanels-LeftPanel');
		if (leftPanel?.classList.contains('is-active'))
		{
			this.togglePanel('left', 'close');
			this.LeftPanelActive = null;
			this.LeftPanelPinned = false;
			document.body.style['marginLeft'] = '0';
		}
		// if the right panel has any pinned-open panels, don't close the wrapper
		const rightPanel = document.getElementById('HeadlightPanels-RightPanel');
		if (rightPanel?.classList.contains('is-active'))
		{
			this.togglePanel('right', 'close');
			this.RightPanelActive = null;
			this.RightPanelPinned = false;
			document.body.style['marginRight'] = '0';
		}
		// we assume that if the user is pressing escape/closing, they want to close any active media
		this.pict.providers.UtilityProvider.pauseAnyActiveMedia();
		this.fable.HeadlightPanels.clearUIHighlights();
	}

	/**
	 * Calls the close handler for the currently active panel based on the given location.
	 * @param {string} location - The panel location identifier (e.g., 'Left', 'Right').
	 * @returns {void}
	 */
	callCloseHandler(location)
	{
	/** @type {string} */
		const activePanelKey = 'ActivePanel' + location;

		/** @type {string | undefined} */
		const activePanelName = this[activePanelKey];

		if (!activePanelName)
		{
			this.pict.log.warn(`No active panel name found for key: ${activePanelKey}`);
			return;
		}

		/** @type {{ name: string, view: string }[]} */
		const panelList = this.pict.settings.HeadlightPanels?.Configuration?.PanelList || [];

		/** @type {{ name: string, view: string } | undefined} */
		const activePanel = panelList.find((panel) => panel.name === activePanelName);

		if (!activePanel)
		{
			this.pict.log.warn(`No panel found in PanelList with name: ${activePanelName}`);
			return;
		}

		/** @type {{ closeHandler?: Function } | undefined} */
		const panelView = this.pict.views[activePanel.view];

		if (panelView && typeof panelView.closeHandler === 'function')
		{
			panelView.closeHandler();
		}
		else
		{
			this.pict.log.warn(`No closeHandler found for panel view: ${activePanel.view}`);
		}
	}


	/**
	 * This function is used to toggle the visibility of both the panel and any content within the panel.
	 * @param {string} pPanelName - The name of the panel to toggle
	 * @param {string} pState - The forced state to toggle the panel to (open or close) - not setting this will just toggle the current state
	 * @param {function} [pCallback] - (optional) The callback function to execute after the panel is toggled
	 * @return {void}
	 * @example
	 * handlePanelToggle('Validations', 'open');
	 * handlePanelToggle('Status', 'close);
	 * handlePanelToggle('Settings');
	 *
	*/
	handlePanelToggle(pPanelName, pState, pCallback)
	{
		const tmpPanelSingular = this.pict.settings.HeadlightPanels.Configuration.PanelList.find((panel) => panel.name === pPanelName.toLowerCase());
		const tmpPanelPlural = this.pict.settings.HeadlightPanels.Configuration.PanelList.find((panel) => panel.name === (pPanelName + 's').toLowerCase());
		// TODO: refactor the panel names to be always singular to handle this stupid quirk better
		const tmpPanel = tmpPanelSingular || tmpPanelPlural;
		if (!tmpPanel)
		{
			this.log.error(`Panel not found in the default configuration: ${pPanelName}`);
			if (pCallback) pCallback();
			return;
		}
		const tmpLocation = tmpPanel.location;
		const tmpLocationCap = (tmpLocation.charAt(0).toUpperCase() + tmpLocation.slice(1));
		const tmpState = pState ? pState : 'toggle';
		// if this is panel is already active, close it
		if (this['ActivePanel' + tmpLocationCap] === tmpPanel.name && tmpState === 'toggle')
		{
			this.callCloseHandler(tmpLocationCap);
			// check if the active panel state on the DOM is active
			if (document.getElementById('HeadlightPanels-' + tmpLocationCap + 'Panel').classList.contains('is-active'))
			{
				this.log.info('Closing active panel');
				this['ActivePanel' + tmpLocationCap] = null;
				this.togglePanel(tmpLocation, 'close');
			}
			else
			{
				this.log.info('Opening active panel');
				this['ActivePanel' + tmpLocationCap] = tmpPanel.name;
				this.togglePanel(tmpLocation, 'open');
			}
			if (pCallback) pCallback();
			return;
		}
		// if a DIFFERENT panel is already active in this location and we're about to swap it out,
		// fire the outgoing panel's closeHandler so it can release any pending state (e.g. clearing
		// `is-loading` buttons on inputs that opened the panel). Without this, togglePanel's
		// "panel is already open" early-return swallows the close lifecycle and the previous panel's
		// pending work is silently abandoned.
		if (this['ActivePanel' + tmpLocationCap] && this['ActivePanel' + tmpLocationCap] !== tmpPanel.name)
		{
			this.callCloseHandler(tmpLocationCap);
		}
		// if we are in mobile view, we need to close the other panel if it's open
		if (this.pict.services.HeadlightPanels.isPhoneDevice)
		{
			// if the other panel is open, close it
			const otherPanelLocation = tmpLocation === 'left' ? 'right' : 'left';
			if (this['ActivePanel' + (otherPanelLocation.charAt(0).toUpperCase() + otherPanelLocation.slice(1))])
			{
				this.log.info(`Closing other panel: ${otherPanelLocation}`);
				this.callCloseHandler(otherPanelLocation.charAt(0).toUpperCase() + otherPanelLocation.slice(1));
				this['ActivePanel' + (otherPanelLocation.charAt(0).toUpperCase() + otherPanelLocation.slice(1))] = null;
				this.togglePanel(otherPanelLocation, 'close');
			}
			// TODO: Revisit this later if we need to deal with iOS Safari and the resize event for the dynamic url bar not respecting 100vh in CSS
			// window.addEventListener('resize', () =>
			// {
			// 	const style = window.getComputedStyle(document.querySelector('#HeadlightPanels-Container'));
			// 	const navBarHeight = style.getPropertyValue('--panels-navbar-height');
			// 	const computedHeightValue = `calc(${ window.innerHeight }px - ${ navBarHeight })`;
			// 	style.setProperty('--panels-panel-height', computedHeightValue);
			// });
		}
		this.log.info(`Rendering panel: ${tmpPanel.name} (${tmpPanel.view}) in ${tmpLocation} panel with forced state: ${tmpState}`);
		// Closing: there is nothing to show, so skip the content render entirely and just close.
		if (tmpState === 'close')
		{
			this['ActivePanel' + tmpLocationCap] = tmpPanel.name;
			this.togglePanel(tmpLocation, tmpState, () =>
			{
				if (pCallback) pCallback();
			});
			return;
		}
		// Open the panel FIRST, then render into it. Rendering first leaves the click with no feedback
		// at all for the whole render -- the panel does not even begin to slide in until the content is
		// ready -- and the slide is then pure added latency instead of cover for the render.
		const tmpPanelElement = document.getElementById(`HeadlightPanels-${tmpLocationCap}Panel`);
		if (!tmpPanelElement?.classList.contains('is-active'))
		{
			// The container still holds the previously opened pane's markup, so a closed panel has to be
			// cleared before it slides in or the user watches stale content arrive.
			this.showPaneLoadingSkeleton(tmpLocation, tmpPanel.name);
		}
		this['ActivePanel' + tmpLocationCap] = tmpPanel.name;
		// pCallback has always meant "content rendered AND the panel finished animating", so hold that
		// contract now that the two run concurrently rather than one after the other.
		let tmpPendingSteps = 2;
		const fStepComplete = () =>
		{
			tmpPendingSteps--;
			if (tmpPendingSteps < 1 && pCallback)
			{
				pCallback();
			}
		};
		this.togglePanel(tmpLocation, tmpState, fStepComplete);
		// A pane that fails mid-render would otherwise leave the panel stuck on the loading skeleton it
		// was just opened with, swallow the open callback, and let the error escape to the click handler.
		// Panes render their tab views in a serial chain, so one bad tab is enough to trigger this.
		let tmpRenderSettled = false;
		const fRenderComplete = (pError) =>
		{
			if (tmpRenderSettled)
			{
				return;
			}
			tmpRenderSettled = true;
			if (pError)
			{
				this.log.error(`Failed to render the ${tmpPanel.name} panel: ${pError?.message || pError}`);
				this.showPaneRenderError(tmpLocation);
			}
			else
			{
				this.log.info(`Rendered ${tmpPanel.name} panel content`);
				this.recordPaneWidth(tmpPanel.name, tmpLocationCap);
			}
			fStepComplete();
		};
		try
		{
			this.pict.views[tmpPanel.view].renderAsync(false, false, false, false, fRenderComplete);
		}
		catch (pError)
		{
			fRenderComplete(pError);
		}
	}

	removeLoadingSkeleton(pElement)
	{
		// if the parent element has the skeleton class, remove it there instead
		this.pict.providers.UtilityProvider.removeLoadingSkeleton(pElement);
	}

	/**
	 * Remember how wide a pane rendered, so its next loading skeleton can hold the panel at that width.
	 * @param {string} pPaneName - The pane that just rendered
	 * @param {string} pLocationCap - The capitalised panel location (Left or Right)
	 * @return {void}
	 */
	recordPaneWidth(pPaneName, pLocationCap)
	{
		const tmpPanel = document.getElementById(`HeadlightPanels-${pLocationCap}Panel`);
		if (!tmpPanel)
		{
			return;
		}
		const tmpWidth = Math.round(tmpPanel.getBoundingClientRect().width);
		if (tmpWidth < 1 || this.PaneWidths[pPaneName] === tmpWidth)
		{
			return;
		}
		this.PaneWidths[pPaneName] = tmpWidth;
		this.savePaneWidths();
	}

	/**
	 * Read the remembered pane widths back.
	 *
	 * Storage can be unavailable (private windows, blocked site data) or hold something unexpected, and
	 * this is only a hint for how wide to draw a loading skeleton, so any failure just starts us empty.
	 * @return {Object} Map of pane name to width in pixels
	 */
	loadPaneWidths()
	{
		try
		{
			const tmpStored = window.localStorage.getItem(_PaneWidthStorageKey);
			if (!tmpStored)
			{
				return {};
			}
			const tmpParsed = JSON.parse(tmpStored);
			if (!tmpParsed || typeof tmpParsed !== 'object' || Array.isArray(tmpParsed))
			{
				return {};
			}
			// a corrupted entry should never get to size a panel
			const tmpWidths = {};
			for (const tmpKey of Object.keys(tmpParsed))
			{
				const tmpValue = tmpParsed[tmpKey];
				if (typeof tmpValue === 'number' && isFinite(tmpValue) && tmpValue > 0)
				{
					tmpWidths[tmpKey] = tmpValue;
				}
			}
			return tmpWidths;
		}
		catch (pError)
		{
			this.log?.info(`Could not read the remembered panel widths: ${pError?.message || pError}`);
			return {};
		}
	}

	/**
	 * Persist the remembered pane widths, so only the first ever open of a pane in this browser draws
	 * its skeleton at the bare minimum width.
	 * @return {void}
	 */
	savePaneWidths()
	{
		try
		{
			window.localStorage.setItem(_PaneWidthStorageKey, JSON.stringify(this.PaneWidths));
		}
		catch (pError)
		{
			// full, disabled or unavailable -- the widths simply will not survive the reload
			this.log?.info(`Could not persist the remembered panel widths: ${pError?.message || pError}`);
		}
	}

	/**
	 * A remembered width, clamped into the band the CSS actually allows at the CURRENT viewport.
	 *
	 * Panes that hit the wrapper's cap remember the cap itself rather than their content, so a width
	 * stored on a wide screen would otherwise draw a skeleton wider than the panel can ever be on a
	 * narrow one.
	 * @param {string} pPaneName - The pane to look up
	 * @return {number} A usable width in pixels, or 0 when nothing is remembered
	 */
	getRememberedPaneWidth(pPaneName)
	{
		const tmpRemembered = this.PaneWidths[pPaneName];
		if (!tmpRemembered)
		{
			return 0;
		}
		const tmpViewportWidth = window.innerWidth || 0;
		if (!tmpViewportWidth)
		{
			return tmpRemembered;
		}
		// mirrors `.panel { min-width: 15vw }` and `.hl-panel-wrapper { max-width: 50vw }`
		return Math.min(Math.max(tmpRemembered, tmpViewportWidth * 0.15), tmpViewportWidth * 0.5);
	}

	/**
	 * Replace a panel's loading skeleton with a failure message.
	 *
	 * Without this a pane that throws mid-render leaves the panel pulsing on the skeleton forever, which
	 * reads as "still loading" rather than "this is broken".
	 * @param {string} pLocation - The panel location to fill (left or right)
	 * @param {string} [pMessage] - The message to display
	 * @return {void}
	 */
	showPaneRenderError(pLocation, pMessage)
	{
		const tmpLocationCap = (pLocation.charAt(0).toUpperCase() + pLocation.slice(1));
		const tmpPanel = document.getElementById(`HeadlightPanels-${tmpLocationCap}Panel`);
		if (!tmpPanel)
		{
			return;
		}
		const tmpNotification = document.createElement('div');
		tmpNotification.className = 'notification has-text-text-light is-dark m-3';
		tmpNotification.textContent = pMessage || 'This panel could not be loaded. Close it and try again.';
		tmpPanel.innerHTML = '';
		tmpPanel.appendChild(tmpNotification);
	}

	/**
	 * Paint a placeholder into a panel container so the panel can be shown before its pane has rendered.
	 *
	 * `.skeleton-block` carries its own min-height and pulse animation, so this needs no extra styling.
	 * @param {string} pLocation - The panel location to fill (left or right)
	 * @param {number} [pRowCount] - The number of placeholder rows to paint
	 * @return {void}
	 */
	showPaneLoadingSkeleton(pLocation, pPaneName, pRowCount)
	{
		const tmpLocationCap = (pLocation.charAt(0).toUpperCase() + pLocation.slice(1));
		const tmpPanel = document.getElementById(`HeadlightPanels-${tmpLocationCap}Panel`);
		if (!tmpPanel)
		{
			this.log.error(`Panel not found for the loading skeleton: [${pLocation}]`);
			return;
		}
		// Stand in for the pane as an actual `.panel`, so the placeholder picks up the panel's height and
		// its heading bar from the styles the real pane will use. Every pane has exactly one
		// `.panel-heading`, so this is always the right shape; the tabs strip that five of them add on
		// top of it is deliberately not reproduced.
		const tmpWrapper = document.createElement('article');
		tmpWrapper.className = 'panel is-active hl-pane-loading-skeleton';
		// The panel wrapper shrinks to fit, and it is normally the rendered pane that gives it a width.
		// Bare skeleton rows are much narrower than that, so without a width here the panel slides in as
		// a sliver and snaps wide when the content lands. Match the pane's own floor, then its last
		// known width when we have one.
		tmpWrapper.style.minWidth = '15vw';
		const tmpLastWidth = pPaneName ? this.getRememberedPaneWidth(pPaneName) : 0;
		if (tmpLastWidth)
		{
			tmpWrapper.style.width = `${tmpLastWidth}px`;
		}
		const tmpHeading = document.createElement('p');
		tmpHeading.className = 'panel-heading';
		// the heading draws from its padding rather than its content, but it still needs something in it
		tmpHeading.textContent = '\u00A0';
		tmpWrapper.appendChild(tmpHeading);
		for (let i = 0; i < (pRowCount || 5); i++)
		{
			const tmpRow = document.createElement('div');
			tmpRow.className = 'panel-block skeleton-block has-background-text-40 mx-3 mb-1';
			tmpWrapper.appendChild(tmpRow);
		}
		tmpPanel.innerHTML = '';
		tmpPanel.appendChild(tmpWrapper);
	}

	/**
	 * Add the theme class to the body to account for the navbar location offset
	 * @return {void}
	 */
	setBodyElementOffsetForFixedNavbar()
	{
		if (this.pict.settings.HeadlightPanels.Configuration.NavBar?.location === 'top')
		{
			document.body.classList.add('has-navbar-fixed-top');
			document.body.classList.remove('has-navbar-fixed-bottom');
		}
		else
		{
			document.body.classList.remove('has-navbar-fixed-top');
			document.body.classList.add('has-navbar-fixed-bottom');
		}
	}

	/**
	 * This function is used to set the default configuration for the view. It will blend any custom configuration with the default configuration.
	 * @return {void}
	 */
	setDefaultPanelConfiguration()
	{
		if (!this.pict.settings.HeadlightPanels)
		{
			this.pict.settings.HeadlightPanels = {};
		}
		this.pict.log.info('Setting default panel configuration.');
		this.pict.settings.HeadlightPanels.Configuration = Object.assign({}, DefaultConfiguration);
		// set the default configuration for reference in the view (for developers to see)
		this.pict.settings.HeadlightPanels.Configuration_Default = DefaultConfiguration;
	}

	/**
	 * This function is used to set the custom configuration for the view to be meshed with the default config on init.
	 * @param {Object} pConfiguration - The default configuration to set
	 * @return {void}
	 */
	setPanelConfiguration(pConfiguration)
	{
		this.pict.log.info('Setting custom panel configuration.');
		if (typeof pConfiguration !== 'object')
		{
			this.pict.log.error('Invalid configuration object provided. Please check the configuration object you provided.');
			return;
		}
		if (!this.pict.settings.HeadlightPanels)
		{
			this.pict.settings.HeadlightPanels = {};
		}
		this.pict.settings.HeadlightPanels.Configuration = { ...DefaultConfiguration, ...pConfiguration };
		// set the custom configuration for reference in the view (for developers to see)
		this.pict.settings.HeadlightPanels.Configuration_Custom = Object.assign({}, pConfiguration);
		// recompile and render the view with the new configuration
		this.pict.views.HeadlightPaneNavigation.compileNavigation();
	}

	/**
	 * @typedef {object} HeadlightToastOptions
	 * @property {string} message - The message to show (HTML permitted; rendered when it looks like markup or allowHTML is set).
	 * @property {string} [type='is-info'] - Legacy Bulma type class (is-info/is-success/is-warning/is-danger) or a PSM enum value.
	 * @property {number} [duration=3000] - Auto-dismiss delay in ms; 0 for a persistent toast.
	 * @property {string} [position='bottom-center'] - PSM position key.
	 * @property {boolean} [dismissible=true] - Show a close button.
	 * @property {boolean} [allowHTML] - Force raw-HTML rendering; auto-detected from the message when omitted.
	 * @property {boolean} [sticky=false] - Keep this toast up until dismissToast() takes it back. Exempt from
	 *   auto-dismiss, from the duplicate throttle, and from single-toast eviction. For state, not events.
	 */

	/**
	 * Resolve the Pict-Section-Modal view, tolerating both registration names in use
	 * (canonical 'Pict-Section-Modal' from the shared scaffold, and form-editor's 'PictSectionModal').
	 * @returns {?object}
	 */
	_resolveModal()
	{
		if (!this.pict || !this.pict.views)
		{
			return null;
		}
		return this.pict.views['Pict-Section-Modal'] || this.pict.views['PictSectionModal'] || null;
	}

	/**
	 * What a keyboard or camera scan does when no panel has taken the scanner: a sample opens Sample Details, and the
	 * Scan Samples cards open Scan Samples. Registered at the bottom of the app's HeadlightQR scan handlers, so an app
	 * (LIMS) or an open panel can handle scans first.
	 */
	registerScanHandling()
	{
		const tmpQR = this.pict.providers.HeadlightQR;
		if (!tmpQR || typeof tmpQR.pushScanHandler !== 'function')
		{
			return;
		}
		tmpQR.pushScanHandler(
			{
				Hash: 'panels-default',
				Mode: 'GoTo',
				onScan: (pResolution) =>
				{
					if (!this.pict.AppData || !this.pict.AppData.CurrentUser || !this.pict.AppData.CurrentUser.LoggedIn)
					{
						return true;
					}
					if (pResolution.Status === 'Found' && pResolution.Entity === 'Sample' && pResolution.Record && pResolution.Record.IDSample)
					{
						this.pict.views.SampleDetails.open(pResolution.Record.IDSample, pResolution.Record);
					}
					else if (pResolution.Status === 'Deleted')
					{
						this.toast({ message: `That ${String(pResolution.Entity || 'record').toLowerCase()} was deleted.`, type: 'is-warning' });
					}
					else if (pResolution.Status !== 'Found')
					{
						this.toast({ message: 'Nothing found for that code.', type: 'is-warning' });
					}
					return true;
				},
			});
		tmpQR.registerActionEntry('cart', 'open', () => this.pict.views.QRPanel.open('putaway'));
		tmpQR.registerActionEntry('receive', 'start', () => this.pict.views.QRPanel.open('receive'));
		const fNeedsPanel = () => this.toast({ message: 'That card is for Scan Samples. Open Scan Samples first, then scan it again.', type: 'is-warning' });
		tmpQR.registerActionEntry('cart', 'submit', fNeedsPanel);
		tmpQR.registerActionEntry('cart', 'clear', fNeedsPanel);
	}

	/**
	 * Show a toast notification to the user, customized based on the options provided.
	 * Delegates to Pict-Section-Modal via HeadlightToastAdapter (replaces the former bulma-toast wrapper).
	 * @param {HeadlightToastOptions} pOptions
	 * @returns {{ dismiss: function }}
	 */
	toast(pOptions)
	{
		const tmpNoop = { dismiss: () => {} };
		// Check if a message was provided, mininum requirement for a toast notification
		if (!pOptions || !pOptions.message)
		{
			this.pict.log.error('No message provided for toast notification');
			return tmpNoop;
		}

		const tmpModal = this._resolveModal();
		if (!tmpModal || typeof tmpModal.toast !== 'function')
		{
			this.pict.log.warn('Pict-Section-Modal unavailable; toast suppressed', { Message: pOptions.message });
			return tmpNoop;
		}

		const tmpPlan = this._ToastAdapter.translate(pOptions, Date.now());
		if (!tmpPlan)
		{
			// Duplicate suppressed by the throttle window.
			return tmpNoop;
		}

		// Single-toast: dismiss any prior toast before showing the new one, then track the new handle.
		this._ToastAdapter.trackHandle(null);
		const tmpHandle = tmpModal.toast(tmpPlan.message, tmpPlan.options);
		this._ToastAdapter.trackHandle(tmpHandle, tmpPlan.sticky);
		return tmpHandle;
	}

	/**
	 * Dismiss a toast this view handed out, releasing any sticky tracking it held.
	 * Tolerates the noop handle toast() returns when the modal view is unavailable or the throttle fired.
	 * @param {?{ dismiss: function }} pHandle - A handle returned by toast().
	 * @returns {void}
	 */
	dismissToast(pHandle)
	{
		this._ToastAdapter.releaseHandle(pHandle);
	}

	/**
	 * Announce a status message to assistive technology.
	 *
	 * Pict-Section-Modal renders toasts with no live-region semantics, so a screen reader never hears
	 * one at all. This keeps a single polite region on the body and rewrites it.
	 * @param {string} pMessage - The message to announce; an empty string clears the region.
	 * @returns {void}
	 */
	announceStatus(pMessage)
	{
		let tmpRegion = document.getElementById('HeadlightPanels-StatusLiveRegion');
		if (!tmpRegion)
		{
			tmpRegion = document.createElement('div');
			tmpRegion.id = 'HeadlightPanels-StatusLiveRegion';
			tmpRegion.className = 'is-sr-only';
			tmpRegion.setAttribute('role', 'status');
			tmpRegion.setAttribute('aria-live', 'polite');
			tmpRegion.setAttribute('aria-atomic', 'true');
			document.body.appendChild(tmpRegion);
		}
		tmpRegion.textContent = pMessage || '';
	}

	/**
	 * This handles the hide/show collapsing of a block group inside a pane of content.
	 * @param {string} pKey - the key of the group we are toggling
	 * @param {string} pState - the state to toggle the group to (open or close)
	 */
	toggleCollapsiblePanelBlockGroup(pKey, pState)
	{
		const group = document.querySelector(`[data-collapsible-toggle="${pKey}"]`);
		const content = document.querySelector(`[data-collapsible-content="${pKey}"]`);
		if (!group || !content)
		{
			return;
		}
		// handle forced open state
		if (pState === 'open')
		{
			content.classList.remove('is-hidden');
			content.classList.add('animate', 'animate__fadeIn', 'animate__faster');
			// if we have a chevron, turn it
			if (group.querySelector('.fa-chevron-right'))
			{
				group.querySelector('.fa-chevron-right').classList.replace('fa-chevron-right', 'fa-chevron-down');
			}
		}
		// handle forced close state
		else if (pState === 'close')
		{
			content.classList.add('is-hidden');
			// if we have a chevron, turn it
			if (group.querySelector('.fa-chevron-down'))
			{
				group.querySelector('.fa-chevron-down').classList.replace('fa-chevron-down', 'fa-chevron-right');
			}
		}
		// handle toggle state
		else
		{
			// if the content is hidden, show it
			if (content.classList.contains('is-hidden'))
			{
				content.classList.remove('is-hidden');
				// if we have a chevron, turn it
				if (group.querySelector('.fa-chevron-right'))
				{
					group.querySelector('.fa-chevron-right').classList.replace('fa-chevron-right', 'fa-chevron-down');
				}
			}
			// if the content is shown, hide it
			else
			{
				content.classList.add('is-hidden');
				// if we have a chevron, turn it
				if (group.querySelector('.fa-chevron-down'))
				{
					group.querySelector('.fa-chevron-down').classList.replace('fa-chevron-down', 'fa-chevron-right');
				}
			}
		}
	}

	/**
	 * This function is used to toggle the visibility of a panel.
	 * @param {string} pLocation - The location of the panel to toggle (left or right)
	 * @param {string} pState - The state to toggle the panel to (open or close)
	 * @param {function} [fCallback] - (optional) The callback function to execute after the panel is toggled
	 * @return {void}
	 * @example
	 * togglePanel('left', 'open');
	 * togglePanel('right', 'close');
	 */
	togglePanel(pLocation, pState, fCallback = () =>
	{})
	{
		if (!pLocation)
		{
			this.log.error('Panel location not found. Please check the location you provided.');
			fCallback();
			return;
		}
		// get the panel and panel wrapper (always capitalize the first letter of the location for the ID look up)
		const tmpLocationCap = (pLocation.charAt(0).toUpperCase() + pLocation.slice(1));
		const tmpPanelID = `HeadlightPanels-${tmpLocationCap}Panel`;
		const tmpPanel = document.getElementById(tmpPanelID);
		// if the panel is not found, log an error and return
		if (!tmpPanel)
		{
			this.log.error(`Panel not found: [${pLocation}; id=${tmpPanelID}]  Please check the name you provided.`);
			fCallback();
			return;
		}
		// if the panel is already active, determine if we're closing it or opening another panel
		const isPanelActive = tmpPanel.classList.contains('is-active');
		if (isPanelActive && pState === 'open')
		{
			this.log.info('Panel is already open');
			this['ActivePanel' + tmpLocationCap] = tmpPanel.id;
			fCallback();
			return;
		}
		if (!isPanelActive && pState === 'close')
		{
			this.log.info('Panel is already closed');
			this['ActivePanel' + tmpLocationCap] = null;
			fCallback();
			return;
		}
		// // account for the lefty mode setting
		// if (this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode)
		// {
		// 	pLocation = pLocation === 'left' ? 'right' : 'left';
		// }
		// if the panel is already active, close it
		if (isPanelActive && pState === 'close')
		{
			this.callCloseHandler(tmpLocationCap);
			this.log.info('Closing panel');
			tmpPanel.classList.remove(this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateIn);
			tmpPanel.classList.add(this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateOut);
			// set a timeout to remove the active/transition classes after the transition duration
			setTimeout(() =>
			{
				this.log.info('Closing panel');
				tmpPanel.classList.remove('is-active');
				tmpPanel.classList.remove(this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateOut);
				this.fable.HeadlightPanels.clearUIHighlights();
				this.fable.HeadlightPanels[pLocation + 'PanelResetCustomWidth']();
				fCallback();
			}, this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateDuration);
		}
		else
		{
			this.log.info('Opening panel');
			// if the panel is not active, open it
			tmpPanel.classList.remove(this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateOut);
			tmpPanel.classList.add(this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateIn);
			tmpPanel.classList.add('is-active');

			// TODO: Revisit these once we separate out "sticky panel that won't close" vs "flex background content shifts when panel opens/closes"
			// for user settings to allow overlapping vs flexing background content (wip)
			// if (this.pict.views.HeadlightPaneSettings.readUserPreference(`PanelOverlap-${pLocation}`))
			// {
			// 	tmpPanel.classList.add('pinned-open');
			// 	document.body.style['margin' + tmpLocationCap] = tmpPanel.offsetWidth + 'px';
			// }
			// else
			// {
			// 	tmpPanel.classList.remove('pinned-open');
			// 	document.body.style['margin' + tmpLocationCap] = 0;
			// }

			// set a timeout to remove the active/transition classes after the transition duration
			setTimeout(() =>
			{
				tmpPanel.classList.remove(this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateIn);
				fCallback();
			}, this.pict.settings.HeadlightPanels.Configuration.PanelLocations[pLocation].animateDuration);
		}
	}

	/**
	 * A general utility function to toggle the visibility of the tab content within a panel.
	 * NOTE: this expects the ID pattern to follow this example:
	 * Tab Elements -> `#HeadlightPanels-{Context}Panel-Tabs`
	 * Tab Buttton -> `#HeadlightPanels-{Context}Panel-TabName`
	 * Tab Content -> `#HeadlightPanels-{Context}Panel-Content`
	 * @param {string} id - the ID of the panel to toggle
	 * @param {string} context - the context of the panel
	 * @return {void}
	 */
	toggleTabContent(id, context)
	{
		if (!id)
		{
			this.log.error('Panel ID not found. Please check the ID you provided.', id);
			return;
		}
		if (!context)
		{
			this.log.error('Panel context not found. Please check the context you provided.', context);
			return;
		}
		const panelID = `HeadlightPanels-${context}Panel`;
		const panel = document.getElementById(panelID);
		if (!panel)
		{
			this.log.error(`Panel not found: [${context}; id=${panelID}]  Please check the name you provided.`, panel);
			return;
		}
		// get the clicked tab and panel
		const tabs = document.getElementById('HeadlightPanels-' + context + 'Panel-Tabs').querySelectorAll('a');
		// remove active class from all tabs
		for (let i = 0; i < tabs.length; i++)
		{
			tabs[i].classList.remove('is-active');
		}
		document.getElementById(id + '-Tab').classList.add('is-active');
		// hide all panels
		const contentList = document.getElementById('HeadlightPanels-' + context + 'Panel').querySelectorAll('.panel-content');
		for (let i = 0; i < contentList.length; i++)
		{
			contentList[i].classList.add('is-hidden');
		}
		const activeContent = document.getElementById(id + '-Content');
		// show the clicked panel
		if (activeContent.classList.contains('is-hidden'))
		{
			activeContent.classList.remove('is-hidden');
		}
		else
		{
			activeContent.classList.add('is-hidden');
		}
	}

	/**
	 * This function is used to toggle the full screen modal with populated content.
	 * @param {string} pState - The forced state to toggle the modal to (open or close) - not setting this will just toggle the current state
	 * @param {Object} pOptions - Optional data to populate the modal with
	 * @return {void}
	 */
	togglePanelFullScreenModal(pState, pOptions)
	{
		const pData = pOptions || {};
		const tmpRenderableHash = pOptions?.RenderableHash || 'HeadlightPanels-FullScreenModal';
		const tmpContentDestinationAddress = pOptions?.ContentDestinationAddress || '#HeadlightPanels-FullScreenModal-Container';
		const tmpCallback = pOptions?.Callback || ((pData) =>
		{
			this.log.info('Rendered Headlight Panels fullscreen modal content with the following optional data:', pData);
		});
		// in case we have any active media playing (video, audio), pause it
		this.pict.providers.UtilityProvider.pauseAnyActiveMedia();
		// handle the forced state or the toggle state
		if (pState === 'close')
		{
			this.AppData.FullScreenModalActive = false;
			document.getElementById('HeadlightPanels-FullScreenModal-Container').innerHTML = '';
		}
		else if (pState === 'open')
		{
			this.AppData.FullScreenModalActive = true;
			this.pict.views.HeadlightPanels.renderAsync(tmpRenderableHash, tmpContentDestinationAddress, pData, false, tmpCallback);
		}
		else
		{
			this.AppData.FullScreenModalActive = !this.AppData.FullScreenModalActive;
			// if the modal is active, render the content
			if (this.AppData.FullScreenModalActive)
			{
				this.pict.views.HeadlightPanels.renderAsync(tmpRenderableHash, tmpContentDestinationAddress, pData, false, tmpCallback);
			}
			// if the modal is not active, clear the content
			else
			{
				document.getElementById('HeadlightPanels-FullScreenModal-Container').innerHTML = '';
			}
		}
	}

	/**
	 * This function is used to toggle the pin state of a panel.
	 * @param {string} pLocation - The location of the panel to toggle the pin state for
	 * @param {string} pContext - The context of the panel to toggle the pin state for
	 * @param {string} pState - The forced state to toggle the pin to (true or false) - not setting this will just toggle the current state
	 * @return {void}
	 */
	togglePanelPinState(pLocation, pContext, pState)
	{
		const locMap =
		{
			'left': 'Left',
			'right': 'Right',
		};
		pLocation = locMap[pLocation] || pLocation;
		const panelWrapperElement = pLocation ? document.getElementById('HeadlightPanels-' + pLocation + 'Panel') : document.getElementById('HeadlightPanels-LeftPanel');
		// get the location from the config
		let locationCap = panelWrapperElement.id.split('-')[1].replace('Panel', '');
		// update our pin state so we can track it in the AppData
		this.AppData[locationCap + 'PanelPinned'] = !this.AppData[locationCap + 'PanelPinned'];

		// account for lefty mode
		if (this.pict.settings.HeadlightPanels.Configuration.PanelOptions.useLeftyMode)
		{
			if (locationCap === 'Left')
			{
				locationCap = 'Right';
			}
			else
			{
				locationCap = 'Left';
			}
		}
		// toggle the pinned-open class
		if (panelWrapperElement.classList.contains('pinned-open') && pState !== 'pin')
		{
			this.log.info('Removing pin from panel');
			panelWrapperElement.classList.remove('pinned-open');
			// reset the body padding
			document.body.style['margin' + locationCap] = 0;
		}
		else if (pState !== 'unpin')
		{
			this.log.info('Pinning panel open');
			panelWrapperElement.classList.add('pinned-open');
			// offset the body padding to account for the pinned panel
			document.body.style['margin' + locationCap] = panelWrapperElement.offsetWidth + 'px';
		}
	}

	/**
	 * This function is used to update the badge on the nav button for a panel.
	 * @param {string} pContext - The context of the panel to update the badge for
	 * @param {number} pLogCount - The number of logs to display in the badge
	 * @return {void}
	 * @example
	 * updateNavButtonBadge('HeadlightPanels-ErrorPanel');
	 */
	updateNavButtonBadge(pContext, pLogCount)
	{
		if (!pContext)
		{
			this.log.error('Panel context not found. Please check the context you provided.');
			return;
		}
		const panelButton = document.getElementById('HeadlightPanels-NavBar-' + pContext + '-Button');
		if (!panelButton)
		{
			this.log.error('Panel button not found. Please check the button you provided.');
			return;
		}
		// TODO: hoist this up to the configuration later
		const panelColorMap = {
			'Solver': 'is-warning',
			'Validations': 'is-danger',
		};
		// get the panel name and log count
		const logCount = pLogCount || 0;
		const navButtonWrapper = panelButton.parentElement;
		// always remove the last badge before adding a new one
		if (navButtonWrapper.querySelector('.badge'))
		{
			navButtonWrapper.removeChild(navButtonWrapper.querySelector('.badge'));
		}
		// only add a badge if there are logs to display
		if (navButtonWrapper && logCount > 0)
		{
			const badge = document.createElement('span');
			badge.classList.add('badge', panelColorMap[pContext] || 'is-info');
			badge.innerText = `${logCount}`;
			navButtonWrapper.append(badge);
		}
	}

	/**
	 * set the width of a panel to a custom (pre-determined) percentage width
	 * @param {String} pDirection - The direction of the panel to toggle
	 * @param {Number} pPercentage - The percentage width to set the panel to
	 * @return {void}
	 */
	setPanelCustomWidth(pDirection, pPercentage)
	{
		const tmpDirections = [ 'left', 'right' ];
		const tmpWidths = [ 0, 20, 25, 33, 40, 50, 60, 67, 75, 80, 100 ];
		// validate the direction before we proceed
		if (!tmpDirections.includes(pDirection))
		{
			this.log.error('Invalid direction provided. Please provide a valid direction. Supported directions are:', tmpDirections);
			return;
		}
		// let's validate the width before we proceed
		if (!tmpWidths.find((width) => width == pPercentage))
		{
			this.log.error('Invalid percentage width provided. Please provide a valid percentage width. Supported widths are:', tmpWidths);
			return;
		}
		// reset the panel width before setting the new width
		this.resetPanelCustomWidth(pDirection);
		// get the panel and set the new width
		const tmpPanel = document.querySelector('.hl-panel-wrapper-' + pDirection);
		if (tmpPanel)
		{
			// remove all the width classes, then add the new width
			tmpPanel.classList.add('is-wide-' + pPercentage);
		}
	}

	/**
	 * Reset the width of a panel to default non-wide width
	 * @param {String} pDirection - The direction of the panel to reset
	 * @return {void}
	 */
	resetPanelCustomWidth(pDirection)
	{
		const tmpDirections = [ 'left', 'right' ];
		const tmpWidths = [ 0, 20, 25, 33, 40, 50, 60, 67, 75, 80, 100 ];
		// validate the direction before we proceed
		if (!tmpDirections.includes(pDirection))
		{
			this.log.error('Invalid direction provided. Please provide a valid direction. Supported directions are:', tmpDirections);
			return;
		}
		// get the panel and remove the width class
		const tmpPanel = document.querySelector('.hl-panel-wrapper-' + pDirection);
		if (tmpPanel)
		{
			// remove all the width classes
			tmpPanel.classList.remove(...tmpWidths.map((width) => 'is-wide-' + width));
		}
	}
}

module.exports = HeadlightPanels;
module.exports.default_configuration = require('../templates/HeadlightPanels-Templates-Default.js');
